/* Runtime authority normalization and projection-safe response reconciliation. */

import { isInlineProgramConsentQuestion } from "./consentQuestionClassification";

const identityValues = (item) => [
  item?.id,
  item?.source_id,
  item?.release_node_id,
].filter((value) => value !== undefined && value !== null && value !== "")
  .map(String);

export const normalizeRuntimeAuthority = (authority) => {
  if (!authority || typeof authority !== "object") return authority;
  const release = authority.release && typeof authority.release === "object"
    ? { ...authority.release }
    : authority.release;
  const sourceKind = String(
    authority.source_kind
    || authority.sourceKind
    || release?.source_kind
    || release?.sourceKind
    || "",
  );
  const sourceId = String(
    authority.source_id
    || authority.sourceId
    || release?.source_id
    || release?.sourceId
    || "",
  );
  return {
    ...authority,
    ...(release ? { release: { ...release, ...(sourceKind ? { source_kind: sourceKind } : {}), ...(sourceId ? { source_id: sourceId } : {}) } } : {}),
    ...(sourceKind ? { source_kind: sourceKind } : {}),
    ...(sourceId ? { source_id: sourceId } : {}),
    authority_identity_complete: Boolean(
      sourceKind && sourceId && authority.session_token,
    ),
  };
};

const stepItems = (session) => {
  const step = session?.step || {};
  return [
    ...(step.questions || []),
    ...(step.consents || []),
    ...(step.sections || []),
    ...(step.programs || []).flatMap((program) => [
      ...(program.questions || []),
      ...(program.checkout_questions || []),
    ]),
  ];
};

const runtimeProgram = (session, programId) => (
  (session?.step?.programs || []).find(
    (program) => String(program?.id || program?.program_id || "") === String(programId || ""),
  ) || null
);

const exactNodeId = (item) => String(
  item?.release_node_id || item?.id || "",
);

const matchesRuntimeQuestion = (question, candidate) => {
  const questionNode = exactNodeId(question);
  const candidateNode = exactNodeId(candidate);
  if (questionNode && candidateNode) return questionNode === candidateNode;
  return Boolean(
    question?.source_id
    && candidate?.source_id
    && String(question.source_id) === String(candidate.source_id)
  );
};

/**
 * Current-boundary projection guard. The immutable questionnaire contains all
 * authored occurrences, while the server step contains only occurrences still
 * interactive for this revision. A child Section already satisfied through a
 * parent or earlier Program occurrence is therefore absent and must stay
 * hidden without deleting its authored placement from the release.
 */
export const questionIsInCurrentRuntimeStep = (question, session) => {
  const state = String(session?.state || "");
  if (state === "treatment_screening" && question?.program_id) {
    const program = runtimeProgram(session, question.program_id);
    if (!program) return false;
    if (question.question_type === "treatment_screening_intro") return true;
    const candidates = program.questions || [];
    return candidates.some((candidate) => (
      matchesRuntimeQuestion(question, candidate)
    ));
  }
  if (state === "common_screening" && question?.scope === "common") {
    const stepQuestions = session?.step?.questions || [];
    const stepSections = session?.step?.sections || [];
    const candidates = [
      ...stepQuestions,
      ...stepSections.flatMap((section) => section?.fields || []),
    ];
    const hasAuthoritativeProjection = Boolean(
      session?.step
      && (
        Object.prototype.hasOwnProperty.call(session.step, "questions")
        || Object.prototype.hasOwnProperty.call(session.step, "sections")
      )
    );
    return !hasAuthoritativeProjection || candidates.some((candidate) => (
      matchesRuntimeQuestion(question, candidate)
    ));
  }
  if (state === "consents" && question?.question_type === "consent") {
    const candidates = session?.step?.consents || [];
    return candidates.some((candidate) => (
      matchesRuntimeQuestion(question, candidate)
    ));
  }
  if (state === "shipping") {
    if (
      question?.question_type !== "shipping_address"
      || question?.location_role !== "shipping"
    ) return false;
    const candidates = session?.step?.questions || [];
    return !Object.prototype.hasOwnProperty.call(session?.step || {}, "questions")
      || candidates.some((candidate) => matchesRuntimeQuestion(question, candidate));
  }
  return true;
};

export const visibleResponseIdentitySet = (questionnaire, session) => {
  const visible = new Set();
  const projectedIdentitySet = new Set();
  stepItems(session).forEach((item) => identityValues(item).forEach((id) => {
    projectedIdentitySet.add(id);
    visible.add(id);
  }));
  (questionnaire?.questions || []).forEach((question) => {
    const type = String(question?.question_type || question?.kind || "").toLowerCase();
    const ids = identityValues(question);
    // Ordinary pending answers may belong to a later authored question that
    // has not reached the current server boundary. Keep them. Legal Consent
    // responses are different: only the currently projected release-node is
    // submit-able and stale values must be removed immediately.
    if (type !== "consent") {
      ids.forEach((id) => visible.add(id));
      return;
    }
    // Inline conditional Consents remain authored Program occurrences.  They
    // are visible with the treatment-screening projection and must survive a
    // restore; only canonical Stage-3 Consents are pruned by the Stage-3 set.
    if (
      session?.state === "treatment_screening"
      && isInlineProgramConsentQuestion(question)
    ) {
      ids.forEach((id) => visible.add(id));
      return;
    }
    if (ids.some((id) => projectedIdentitySet.has(id))) {
      ids.forEach((id) => visible.add(id));
    }
  });
  return visible;
};

export const pruneResponsesToProjection = (responses, questionnaire, session) => {
  const next = { ...(responses && typeof responses === "object" ? responses : {}) };
  const visible = visibleResponseIdentitySet(questionnaire, session);
  const consentAliases = new Set();
  (questionnaire?.questions || []).forEach((question) => {
    const type = String(question?.question_type || question?.kind || "").toLowerCase();
    if (type === "consent") identityValues(question).forEach((id) => consentAliases.add(id));
  });
  Object.keys(next).forEach((key) => {
    if (consentAliases.has(String(key)) && !visible.has(String(key))) delete next[key];
  });
  return next;
};
